import React, { useState, useEffect } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Redirect } from "react-router-dom";
import NumberFormat from "react-number-format";
import Clipboard from "react-clipboard.js";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Container from "@material-ui/core/Container";
import MobileStepper from "@material-ui/core/MobileStepper";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Alert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { withContext } from "../context";
import logger from "../logger";
import { CART_STATUS } from "../constants";
import { formatRut } from "../functions";
import { getCartFull } from "../graphql/queries";
import Loading from "../components/Loading";
import FileUploader from "../components/FileUploader";

const stepsKeys = {
  intro: "intro",
  info: "info",
  receipt: "receipt"
};

const steps = [
  { key: stepsKeys.intro, title: "¿Cómo hacerlo?" },
  { key: stepsKeys.info, title: "Datos bancarios de la Tienda" },
  { key: stepsKeys.receipt, title: "Adjunta tu comprobante de pago" }
];

const CopyButton = ({ label, value, success, onSuccess }) => {
  return (
    <Clipboard
      className={`button-copy ${success && "success"}`}
      data-clipboard-text={value}
      onSuccess={onSuccess}
    >
      <span>{label}</span>
      <span className="icon">
        {success && <span className="mr-3">Copiado!</span>}
        <FileCopyIcon fontSize="small" />
      </span>
    </Clipboard>
  );
};

const WireTransfer = ({ history, store, useStoreSlug, cartID }) => {
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(0);
  const [cart, setCart] = useState(null);
  const [redirect, setRedirect] = useState(null);
  const [openConfirmCancel, setOpenConfirmCancel] = React.useState(false);
  const [file, setFile] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    const fetchCart = async () => {
      try {
        const vars = {
          id: cartID
        };
        const result = await API.graphql(graphqlOperation(getCartFull, vars));

        if (result.data.getCart) {
          const delivery = JSON.parse(result.data.getCart.delivery.details);
          const payment = JSON.parse(result.data.getCart.payment.details);
          const ticket = JSON.parse(result.data.getCart.ticket);
          const items = JSON.parse(result.data.getCart.items);

          const cart = {
            ...result.data.getCart,
            delivery: {
              ...result.data.getCart.delivery,
              details: delivery
            },
            payment: {
              ...result.data.getCart.payment,
              details: payment
            },
            ticket,
            items
          };

          if (!(cart.status === CART_STATUS.onPayment && cart.payment.type === "transferencia")) {
            history.push(`${useStoreSlug ? `/${store.slug}` : ""}/comprobante/${cart.id}`);
          }

          setCart(cart);
        } else {
          setRedirect(useStoreSlug ? `/${store.slug}` : "");
        }
      } catch (err) {
        logger("WireTransfer.fetchingCartFull.error", err.message);
        setRedirect(useStoreSlug ? `/${store.slug}` : "");
      }
    };

    if (cartID) {
      fetchCart();
    }
  }, [cartID, store, useStoreSlug, history]);

  const validateReceipt = () => {
    return file !== null;
  };

  const validate = () => {
    switch (steps[step].key) {
      case stepsKeys.intro:
      case stepsKeys.info:
        return true;
      case stepsKeys.receipt:
        return validateReceipt();
      default:
        return false;
    }
  };

  const cancel = async () => {
    setSending(true);
    setOpenConfirmCancel(false);

    try {
      const init = {
        body: {
          cartID
        }
      };

      await API.del("carritotransferencia", "/transferencia", init);

      history.push(`${useStoreSlug ? `/${store.slug}` : ""}/comprobante/${cartID}`);
    } catch (err) {
      logger("WireTransfer.cancel.error", err);
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    setSending(true);

    try {
      const key = `${store.id}/${cartID}/transferencia/${file.name}.${file.extension}`;

      const initUrl = {
        body: {
          key,
          type: file.data.type
        }
      };

      const urlData = await API.post("carritofacilapi", "/store/uploader", initUrl);

      let url = undefined;
      let uploaded = false;

      if (urlData.success && urlData.url) {
        url = urlData.url;
      }

      if (url) {
        const initPut = {
          method: "PUT",
          headers: {
            "x-amz-acl": "public-read"
          },
          body: file.data
        };
        const upladedData = await fetch(urlData.url, initPut);

        if (upladedData.ok) {
          uploaded = true;
        }
      }

      if (uploaded) {
        const comprobante = {
          key: key,
          name: file.name,
          extension: file.extension,
          size: file.data.size,
          type: file.data.type,
          bucket: process.env.REACT_APP_CF_BUCKET
        };

        const init = {
          body: {
            cartID,
            comprobante
          }
        };

        await API.post("carritotransferencia", "/transferencia", init);

        history.push(`${useStoreSlug ? `/${store.slug}` : ""}/comprobante/${cartID}`);
      }
    } catch (err) {
      logger("WireTransfer.send.error", err);
    } finally {
      setSending(false);
    }
  };

  const next = () => {
    switch (steps[step].key) {
      case stepsKeys.intro:
      case stepsKeys.info:
        setStep(s => ++s);
        break;
      case stepsKeys.receipt:
        send();
        break;
      default:
        break;
    }
  };

  const copySuccess = name => {
    setCopied(name);

    setTimeout(() => {
      setCopied("");
    }, 2000);
  };

  const renderIntro = () => {
    return (
      <>
        <List className="mt-2">
          <ListItem>
            <ListItemAvatar>
              <Avatar className="with-background">1</Avatar>
            </ListItemAvatar>
            <ListItemText primary="Ingresa a la web o app de tu banco." />
          </ListItem>

          <ListItem>
            <ListItemAvatar>
              <Avatar className="with-background">2</Avatar>
            </ListItemAvatar>
            <ListItemText primary="Agrega un nuevo destinatario con los datos bancarios de la Tienda." />
          </ListItem>

          <ListItem>
            <ListItemAvatar>
              <Avatar className="with-background">3</Avatar>
            </ListItemAvatar>
            <ListItemText primary="Adjunta tu comprobante de pago." />
          </ListItem>
        </List>

        <Alert severity="info" className="mt-4">
          <strong>Recuerda</strong>, la transferencia debe ser hecha en este momento, de lo
          contrario tu pedido será rechazado.
        </Alert>
      </>
    );
  };

  const renderInfo = () => {
    const {
      body: {
        config: { email, recaudacion }
      }
    } = store;

    return (
      <>
        <div className="mt-4">
          <Typography variant="body2">Monto a transferir</Typography>
          <CopyButton
            label={
              <NumberFormat
                value={cart.payment.details.total}
                thousandSeparator="."
                decimalSeparator=","
                prefix="$"
                displayType={"text"}
              />
            }
            value={cart.payment.details.total}
            success={copied === "monto"}
            onSuccess={() => copySuccess("monto")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Nombre</Typography>
          <CopyButton
            label={recaudacion.titular}
            value={recaudacion.titular}
            success={copied === "titular"}
            onSuccess={() => copySuccess("titular")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Rut</Typography>
          <CopyButton
            label={formatRut(recaudacion.rut)}
            value={recaudacion.rut}
            success={copied === "rut"}
            onSuccess={() => copySuccess("rut")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Email</Typography>
          <CopyButton
            label={email}
            value={email}
            success={copied === "email"}
            onSuccess={() => copySuccess("email")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Banco</Typography>
          <CopyButton
            label={recaudacion.banco.label}
            value={recaudacion.banco.label}
            success={copied === "banco"}
            onSuccess={() => copySuccess("banco")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Tipo de cuenta</Typography>
          <CopyButton
            label={recaudacion.tipoCuenta.label}
            value={recaudacion.tipoCuenta.label}
            success={copied === "tipoCuenta"}
            onSuccess={() => copySuccess("tipoCuenta")}
          />
        </div>

        <div className="mt-3">
          <Typography variant="body2">Nº de cuenta</Typography>
          <CopyButton
            label={recaudacion.cuenta}
            value={recaudacion.cuenta}
            success={copied === "cuenta"}
            onSuccess={() => copySuccess("cuenta")}
          />
        </div>
      </>
    );
  };

  const renderReceipt = () => {
    return (
      <>
        <Alert severity="info" className="mt-3">
          Puedes subir una fotografía o un comprobante en formato PDF.
        </Alert>

        <FileUploader className="fileuploader mt-3" onChange={setFile} />
      </>
    );
  };

  const renderSteps = () => {
    switch (steps[step].key) {
      case stepsKeys.intro:
        return renderIntro();
      case stepsKeys.info:
        return renderInfo();
      case stepsKeys.receipt:
        return renderReceipt();
      default:
        return null;
    }
  };

  const renderConfirmCancel = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        open={openConfirmCancel}
        onClose={() => setOpenConfirmCancel(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Cancelar el pedido</DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Seguro que desea cancelar el pedido?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConfirmCancel(false)} color="primary">
            No
          </Button>
          <Button onClick={cancel} color="primary" autoFocus>
            Sí
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderButtonText = () => {
    if (sending) return "Enviando";

    switch (steps[step].key) {
      case stepsKeys.intro:
        return "Ver datos de transferencia";
      case stepsKeys.info:
        return "Adjuntar comprobante";
      case stepsKeys.receipt:
        return "Notificar pago";
      default:
        return null;
    }
  };

  if (!cart || !store) {
    return (
      <>
        <Loading fullscreen />
        {redirect && <Redirect to={redirect} />}
      </>
    );
  }

  return (
    <>
      {renderConfirmCancel()}

      <AppBar color="default" position="sticky" className="app-top-bar">
        <Toolbar className="justify-content-between">
          {step > 0 ? (
            <IconButton edge="start" onClick={() => setStep(s => --s)}>
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <div style={{ width: "35px", height: "35px" }}></div>
          )}

          <Typography component="span" variant="h6">
            {sending ? <Loading circularProgressSize={20} /> : "Pago con transferencia"}
          </Typography>

          <Tooltip placement="left" title="Cancelar el pedido">
            <IconButton aria-label="close" onClick={() => setOpenConfirmCancel(true)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xs" className="mt-4 mb-8">
        <MobileStepper
          className="my-4"
          variant="progress"
          steps={steps.length + 2}
          position="top"
          activeStep={step + 1}
        />

        <Typography variant="h5" align="center">
          {steps[step].title}
        </Typography>

        {renderSteps()}

        <Button
          fullWidth
          className="mt-5 mb-4 wide"
          variant="contained"
          size="large"
          disabled={!validate() || sending}
          onClick={next}
        >
          {renderButtonText()}
        </Button>
      </Container>
    </>
  );
};

export default withContext(WireTransfer);
